import { useState, useEffect, useCallback } from 'react'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import ModalListaClientes from '../components/ModalListaClientes'
import { clientesApi } from '../api/notasVenta'
import { fmtFechaHora } from '../utils/fecha'
import { colors, text, space, sx, radius } from '../design/tokens'

/**
 * CLIENTES → Lista negra. Clientes bloqueados: no dejan pasar NADA (NV, remito,
 * presupuesto, cobranza, envío a Presea, importación Magento). Gestión: agregar
 * con motivo (lupa de cliente) y quitar. El bloqueo se aplica en backend.
 *
 * Abajo, IPs bloqueadas (11 oct 2026): cortan la entrada a la tienda desde
 * esa IP SIN mandar a nadie a lista negra — la compra hecha a nombre de otra
 * persona. También se cargan con "🌐 Bloquear IP" en la NV.
 *
 * Al final, marcas restringidas por fraude (29 oct 2026; la restricción es
 * por marca): la tienda no deja pagar con Mobbex sus productos (salvo clientes reconocidos),
 * y las sugerencias del relevamiento semanal. Regla: PagoRestringidoService.
 */
export default function ListaNegraPage() {
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [picker, setPicker] = useState(false)
  const [aBloquear, setABloquear] = useState(null)   // cliente elegido en la lupa
  const [motivo, setMotivo] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [ips, setIps] = useState([])
  const [ipNueva, setIpNueva] = useState('')
  const [ipMotivo, setIpMotivo] = useState('')
  const [restr, setRestr] = useState({ lista: [], sugerencias: [], activo: true })
  const [restrValor, setRestrValor] = useState('')
  const [restrMotivo, setRestrMotivo] = useState('')

  const cargar = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [{ data }, { data: dataIps }, { data: dataRestr }] = await Promise.all([clientesApi.listaNegra(), clientesApi.ipsBloqueadas(), clientesApi.restringidos()])
      setClientes(data)
      setIps(dataIps)
      setRestr(dataRestr)
    } catch {
      setError('No se pudo cargar la lista negra.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const confirmarBloqueo = async () => {
    if (!aBloquear) return
    setGuardando(true); setError('')
    try {
      await clientesApi.bloquear(aBloquear.id, motivo || null)
      setABloquear(null); setMotivo('')
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo bloquear el cliente.')
    } finally { setGuardando(false) }
  }

  const desbloquear = async (c) => {
    if (!window.confirm(`¿Sacar a ${c.nombre} de la lista negra? Vuelve a poder operar normalmente.`)) return
    try {
      await clientesApi.desbloquear(c.id)
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo desbloquear el cliente.')
    }
  }

  const bloquearIp = async () => {
    if (!ipNueva.trim()) return
    setError('')
    try {
      await clientesApi.bloquearIp(ipNueva.trim(), ipMotivo.trim() || null)
      setIpNueva(''); setIpMotivo('')
      await cargar()
    } catch (e) {
      setError(e.response?.data?.errors?.ip?.[0] || e.response?.data?.message || 'No se pudo bloquear la IP.')
    }
  }

  const desbloquearIp = async (b) => {
    if (!window.confirm(`¿Desbloquear la IP ${b.ip}? Vuelve a poder entrar a la tienda.`)) return
    try {
      await clientesApi.desbloquearIp(b.id)
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo desbloquear la IP.')
    }
  }

  const restringir = async () => {
    if (!restrValor.trim()) return
    setError('')
    try {
      await clientesApi.restringir('marca', restrValor.trim(), restrMotivo.trim() || null)
      setRestrValor(''); setRestrMotivo('')
      await cargar()
    } catch (e) {
      setError(e.response?.data?.errors?.valor?.[0] || e.response?.data?.message || 'No se pudo agregar a la lista.')
    }
  }

  const quitarRestringido = async (r) => {
    if (!window.confirm(`¿Sacar ${r.etiqueta || r.valor} de la lista? Vuelve a poder pagarse con Mobbex en la tienda.`)) return
    try {
      await clientesApi.quitarRestringido(r.id)
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo sacar de la lista.')
    }
  }

  const resolverSugerencia = async (s, accion) => {
    try {
      await clientesApi.resolverSugerenciaRestringido(s.id, accion)
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo resolver la sugerencia.')
    }
  }

  return (
    <div>
      <div style={st.header}>
        <div>
          <h1 style={st.title}>Lista negra</h1>
          <div style={st.sub}>Clientes bloqueados — no se les puede crear NV, remito, presupuesto, cobranza ni enviar nada a Presea.</div>
        </div>
        <Boton variante="destructive" onClick={() => setPicker(true)}>🚫 Agregar cliente</Boton>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      <div style={sx.card}>
        {loading ? (
          <div style={st.empty}>Cargando…</div>
        ) : clientes.length === 0 ? (
          <div style={st.empty}>No hay clientes en lista negra.</div>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Código</th>
                <th style={sx.th}>Cliente</th>
                <th style={sx.th}>CUIT</th>
                <th style={sx.th}>Localidad</th>
                <th style={sx.th}>Motivo</th>
                <th style={sx.th}>Bloqueado</th>
                <th style={{ ...sx.th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {clientes.map(c => (
                <tr key={c.id}>
                  <td style={{ ...sx.td, ...sx.mono }}>{c.codigo_presea || '—'}</td>
                  <td style={sx.td}>{c.nombre}</td>
                  <td style={{ ...sx.td, ...sx.mono }}>{c.cuit || '—'}</td>
                  <td style={sx.td}>{c.localidad || '—'}</td>
                  <td style={{ ...sx.td, color: colors.textSec }}>{c.lista_negra_motivo || '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>
                    {c.lista_negra_at ? fmtFechaHora(c.lista_negra_at) : '—'}
                    {c.lista_negra_por?.nombre ? ` · ${c.lista_negra_por.nombre}` : ''}
                  </td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>
                    <Boton variante="secondary" sm onClick={() => desbloquear(c)}>Quitar</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={st.seccion}>
        <h2 style={st.subtitle}>IPs bloqueadas</h2>
        <div style={st.sub}>No entran más a ligier.com.ar desde esa IP. El cliente a cuyo nombre se hizo la compra NO va a lista negra: puede seguir comprando.</div>
      </div>
      <div style={st.formIp}>
        <input style={st.input} placeholder="IP (ej. 181.44.129.165)" value={ipNueva} onChange={e => setIpNueva(e.target.value)} />
        <input style={{ ...st.input, flex: 2 }} placeholder="Motivo (opcional)" value={ipMotivo} onChange={e => setIpMotivo(e.target.value)} />
        <Boton variante="destructive" disabled={!ipNueva.trim()} onClick={bloquearIp}>🌐 Bloquear IP</Boton>
      </div>
      <div style={sx.card}>
        {loading ? (
          <div style={st.empty}>Cargando…</div>
        ) : ips.length === 0 ? (
          <div style={st.empty}>No hay IPs bloqueadas.</div>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>IP</th>
                <th style={sx.th}>Pedido</th>
                <th style={sx.th}>Motivo</th>
                <th style={sx.th}>Bloqueada</th>
                <th style={{ ...sx.th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {ips.map(b => (
                <tr key={b.id}>
                  <td style={{ ...sx.td, ...sx.mono }}>{b.ip}</td>
                  <td style={{ ...sx.td, ...sx.mono }}>{b.pedido_web?.numero || '—'}</td>
                  <td style={{ ...sx.td, color: colors.textSec }}>{b.motivo || '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>
                    {fmtFechaHora(b.created_at)}
                    {b.bloqueada_por?.nombre ? ` · ${b.bloqueada_por.nombre}` : ''}
                  </td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>
                    <Boton variante="secondary" sm onClick={() => desbloquearIp(b)}>Quitar</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={st.seccion}>
        <h2 style={st.subtitle}>Marcas restringidas por fraude</h2>
        <div style={st.sub}>
          En ligier.com.ar un carrito con un producto de alguna de estas marcas (todas sus variantes) no se paga con tarjeta por Mobbex: quedan Mercado Pago, transferencia y a convenir, y no entra en las cuotas sin interés.
          Los clientes reconocidos (con cuenta y al menos 2 compras pagas de hace más de 45 días) compran normal. Las membresías del Wine Club no se tocan.
          {!restr.activo && <strong style={{ color: colors.err }}> · La restricción está APAGADA.</strong>}
        </div>
      </div>

      {restr.sugerencias.length > 0 && (
        <div style={{ ...sx.card, marginBottom: space.lg }}>
          <div style={st.sugTitulo}>Sugerencias del relevamiento semanal</div>
          <table style={sx.table}>
            <tbody>
              {restr.sugerencias.map(s => (
                <tr key={s.id}>
                  <td style={sx.td}>{s.accion === 'agregar' ? '➕ Restringir' : '➖ Sacar'}</td>
                  <td style={sx.td}>{s.etiqueta}</td>
                  <td style={{ ...sx.td, color: colors.textSec }}>{s.detalle}</td>
                  <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Boton variante="primary" sm onClick={() => resolverSugerencia(s, 'aceptar')}>{s.accion === 'agregar' ? 'Restringir' : 'Sacar'}</Boton>{' '}
                    <Boton variante="ghost" sm onClick={() => resolverSugerencia(s, 'ignorar')}>Ignorar</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={st.formIp}>
        <input style={st.input} placeholder="Marca (ej. Jack Daniel's)" value={restrValor} onChange={e => setRestrValor(e.target.value)} />
        <input style={{ ...st.input, flex: 2 }} placeholder="Motivo (opcional)" value={restrMotivo} onChange={e => setRestrMotivo(e.target.value)} />
        <Boton variante="destructive" disabled={!restrValor.trim()} onClick={restringir}>🔒 Restringir</Boton>
      </div>
      <div style={sx.card}>
        {loading ? (
          <div style={st.empty}>Cargando…</div>
        ) : restr.lista.length === 0 ? (
          <div style={st.empty}>No hay marcas restringidas.</div>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Marca</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Productos</th>
                <th style={sx.th}>Motivo</th>
                <th style={sx.th}>Agregado</th>
                <th style={{ ...sx.th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {restr.lista.map(r => (
                <tr key={r.id}>
                  <td style={sx.td}>{(r.etiqueta || r.valor).replace(/^Marca /, '')}</td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{r.productos}</td>
                  <td style={{ ...sx.td, color: colors.textSec }}>{r.motivo || '—'}</td>
                  <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>
                    {fmtFechaHora(r.created_at)}
                    {r.agregado_por?.nombre ? ` · ${r.agregado_por.nombre}` : r.origen === 'relevamiento' ? '' : r.origen === 'inicial' ? ' · lista inicial' : ''}
                  </td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>
                    <Boton variante="secondary" sm onClick={() => quitarRestringido(r)}>Quitar</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {picker && (
        <ModalListaClientes
          onSelect={c => { setABloquear(c); setMotivo(''); setPicker(false) }}
          onClose={() => setPicker(false)}
        />
      )}

      {aBloquear && (
        <Modal title="Agregar a lista negra" onClose={() => setABloquear(null)} maxWidth="480px">
          <div style={st.modalBody}>
            <div style={st.clienteInfo}>
              <strong>{aBloquear.nombre}</strong>
              <span style={st.meta}>Nº {aBloquear.codigo_presea || '—'}{aBloquear.cuit ? ` · ${aBloquear.cuit}` : ''}</span>
            </div>
            <div>
              <label style={st.label}>Motivo (opcional)</label>
              <textarea style={st.textarea} rows={3} value={motivo} onChange={e => setMotivo(e.target.value)}
                placeholder="Ej: mora histórica, fraude, pedido de la gerencia…" />
            </div>
            <div style={st.modalActions}>
              <Boton variante="ghost" onClick={() => setABloquear(null)}>Cancelar</Boton>
              <Boton variante="destructive" disabled={guardando} onClick={confirmarBloqueo}>
                {guardando ? 'Bloqueando…' : '🚫 Bloquear cliente'}
              </Boton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: space.xs },
  seccion: { marginTop: space['3xl'], marginBottom: space.lg },
  subtitle: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  formIp: { display: 'flex', gap: space.md, marginBottom: space.lg, flexWrap: 'wrap' },
  sugTitulo: { fontWeight: 600, fontSize: text.sec, color: colors.text, padding: `${space.md}px ${space.lg}px 0` },
  input: { flex: 1, minWidth: 160, padding: `${space.md}px ${space.lg}px`, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, boxSizing: 'border-box' },
  errorBox: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, marginBottom: space.lg },
  empty: { color: colors.textMeta, textAlign: 'center', padding: `${space.xl}px 0`, fontSize: text.sec },
  modalBody: { display: 'flex', flexDirection: 'column', gap: space.lg },
  clienteInfo: { background: colors.linkBg, border: `1px solid #bfdbfe`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: 2 },
  meta: { ...sx.mono, fontSize: text.sec, color: colors.textSec },
  label: { display: 'block', fontWeight: 600, fontSize: text.sec, color: colors.text, marginBottom: space.sm },
  textarea: { width: '100%', padding: `${space.md}px ${space.lg}px`, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, boxSizing: 'border-box', resize: 'vertical' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: space.md },
}
